/**
 * REQ-04：选了知识库模板但无有效工时规则时的对话状态机（R3）。
 * 未选模板 / 走 Agent 默认时不进入本流程。
 */

export type WbsHoursDialoguePhase =
  | 'idle'
  | 'await_wbs_confirm'
  | 'await_hours_rules'
  | 'await_hours_confirm'
  | 'done';

export interface WbsTemplateRef {
  knowledgeDocId?: string;
  knowledgeBaseId?: string;
  title?: string;
  version?: string;
}

export interface WbsHoursDialogueState {
  projectId: number;
  phase: WbsHoursDialoguePhase;
  templateTitle?: string;
  pendingRulesText?: string;
}

const STORAGE_PREFIX = 'aipd.wbsHoursDialogue.';

export function hasSelectedWbsTemplate(ref?: WbsTemplateRef | null): boolean {
  if (!ref) return false;
  return Boolean(String(ref.knowledgeDocId || '').trim() || String(ref.title || '').trim());
}

/** R3 触发：已选模板 + 本系统判定工时需确认 */
export function shouldStartHoursDialogue(ctx: {
  wbsTemplateRef?: WbsTemplateRef | null;
  hoursPlan?: { needsConfirm?: boolean; reason?: string } | null;
  constraints?: Record<string, string> | null;
}): boolean {
  if (!hasSelectedWbsTemplate(ctx.wbsTemplateRef)) return false;
  if (ctx.hoursPlan?.needsConfirm === true) return true;
  return Boolean(String(ctx.constraints?.hoursPlanNeedsConfirm || '').trim());
}

export function wbsConfirmPrompt(templateTitle?: string): string {
  const tpl = String(templateTitle || '').trim();
  const head = tpl
    ? `已按知识库模板「${tpl}」生成 WBS，但未读到有效工时规则。`
    : '已生成 WBS，但所选模板未读到有效工时规则。';
  return (
    `${head}\n\n` +
    '请先确认任务结构（阶段 / 任务 / 交付物 / 前置关系）是否合适。\n' +
    '确认后请回复「确认WBS」；若需调整可先在 WBS 视图中手动修改，再回复确认。\n' +
    '确认后我将协助补充工时规则并回填任务工时。'
  );
}

export function hoursRulesPrompt(): string {
  return (
    'WBS 内容已确认。接下来请补充工时规则，可用以下任一方式：\n' +
    '1）按行描述：任务名,基线天数（例如：立项评审,5）\n' +
    '2）说明计算方式与基线总天数、特殊任务例外\n' +
    '3）回复「使用默认工时」表示接受本次 Agent 已回填的默认工时，结束补充\n\n' +
    '补充完成后我会请你确认拟采用的规则，再请求回填。'
  );
}

export function hoursConfirmPrompt(rulesSummary: string): string {
  return (
    `拟采用的工时规则如下：\n${rulesSummary}\n\n` +
    '请回复「确认工时规则」以按此规则回填；或继续补充修改。'
  );
}

export function isWbsConfirmReply(text: string): boolean {
  const s = String(text || '').trim();
  if (!s) return false;
  return /确认\s*WBS|WBS\s*确认|确认任务结构|结构没问题|可以生成工时/i.test(s);
}

export function isUseDefaultHoursReply(text: string): boolean {
  const s = String(text || '').trim();
  return /使用默认工时|接受默认|默认规则|不用补充/.test(s);
}

export function isHoursConfirmReply(text: string): boolean {
  const s = String(text || '').trim();
  return /确认工时规则|确认规则|按此回填|开始回填/.test(s);
}

/** 简易解析「任务名,天数」或「任务名\t天数」多行 */
export function parseHoursConfigRowsFromText(text: string): Array<Record<string, unknown>> {
  const rows: Array<Record<string, unknown>> = [];
  const lines = String(text || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  for (const line of lines) {
    if (/^(任务|task)/i.test(line) && /基线|天数|days/i.test(line)) continue;
    const m = line.match(/^(.+?)[,，\t|]\s*(-?\d+(?:\.\d+)?)\s*(?:天|日|d)?\s*$/i);
    if (!m) continue;
    const taskName = m[1].trim().replace(/^[-*•]\s*/, '');
    const days = Number(m[2]);
    if (!taskName || !(days > 0)) continue;
    rows.push({ 任务: taskName, 基线天数: days });
  }
  return rows;
}

export function summarizeHoursRows(rows: Array<Record<string, unknown>>): string {
  if (!rows.length) return '（无结构化行，将按你的文字说明交由后续对话确认）';
  return rows
    .slice(0, 20)
    .map((r) => `- ${r['任务'] ?? r.taskName ?? '?'}：${r['基线天数'] ?? r.baselineDays ?? '?'} 天`)
    .join('\n');
}

export function loadDialogueState(projectId: number): WbsHoursDialogueState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + projectId);
    if (!raw) return null;
    return JSON.parse(raw) as WbsHoursDialogueState;
  } catch {
    return null;
  }
}

export function saveDialogueState(state: WbsHoursDialogueState): void {
  try {
    sessionStorage.setItem(STORAGE_PREFIX + state.projectId, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

export function clearDialogueState(projectId: number): void {
  try {
    sessionStorage.removeItem(STORAGE_PREFIX + projectId);
  } catch {
    /* ignore */
  }
}

export function startAwaitWbsConfirm(projectId: number, templateTitle?: string): WbsHoursDialogueState {
  const state: WbsHoursDialogueState = {
    projectId,
    phase: 'await_wbs_confirm',
    templateTitle,
  };
  saveDialogueState(state);
  return state;
}
